import React, { useEffect, useMemo, useState } from "react";

import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Collapse,
    Divider,
    Link,
    Typography
} from "@material-ui/core";

import { useLanguage } from "contexts/LanguageContext";
import { useAuth } from "auth/AuthContext";

const DEFAULT_LANGUAGE = "en";

const LANGUAGE_LOCALES = {
    en: "en-GB",
    de: "de-DE",
    fr: "fr-FR",
    es: "es-ES",
    uk: "uk-UA",
    tr: "tr-TR"
};

const UI_TEXTS = {
    en: {
        unknownProvider: "Unknown provider",
        noTitle: "Untitled",
        openOriginal: "Open original",
        recommendation: "Recommendation",
        noAnalysis: "No analysis available.",
        analysisJsonInvalid: "The analysis JSON could not be read.",
        action: "Action",
        confidence: "Confidence",
        horizon: "Horizon",
        analysis: "Analysis",
        rationale: "Rationale",
        tags: "Tags",
        tickers: "Tickers",
        showAnalysisRaw: "Show analysis raw data",
        hideAnalysisRaw: "Hide analysis raw data",
        showTranslationsRaw: "Show translation raw data",
        hideTranslationsRaw: "Hide translation raw data",
        unknown: "Unknown",
        actionBuy: "BUY",
        actionSell: "SELL",
        actionHold: "HOLD",
        horizonShort: "Short",
        horizonMedium: "Medium",
        horizonLong: "Long",
        horizonIntra: "Intraday"
    },

    de: {
        unknownProvider: "Unbekannter Anbieter",
        noTitle: "Ohne Titel",
        openOriginal: "Original öffnen",
        recommendation: "Empfehlung",
        noAnalysis: "Keine Analyse vorhanden.",
        analysisJsonInvalid:
            "Das Analyse-JSON konnte nicht gelesen werden.",
        action: "Aktion",
        confidence: "Konfidenz",
        horizon: "Zeithorizont",
        analysis: "Analyse",
        rationale: "Begründung",
        tags: "Tags",
        tickers: "Ticker",
        showAnalysisRaw: "Analyse-Rohdaten anzeigen",
        hideAnalysisRaw: "Analyse-Rohdaten ausblenden",
        showTranslationsRaw: "Übersetzungs-Rohdaten anzeigen",
        hideTranslationsRaw: "Übersetzungs-Rohdaten ausblenden",
        unknown: "Unbekannt",
        actionBuy: "KAUFEN",
        actionSell: "VERKAUFEN",
        actionHold: "HALTEN",
        horizonShort: "Kurzfristig",
        horizonMedium: "Mittelfristig",
        horizonLong: "Langfristig",
        horizonIntra: "Intraday"
    },

    fr: {
        unknownProvider: "Fournisseur inconnu",
        noTitle: "Sans titre",
        openOriginal: "Ouvrir l’original",
        recommendation: "Recommandation",
        noAnalysis: "Aucune analyse disponible.",
        analysisJsonInvalid:
            "Le JSON de l’analyse n’a pas pu être lu.",
        action: "Action",
        confidence: "Confiance",
        horizon: "Horizon",
        analysis: "Analyse",
        rationale: "Justification",
        tags: "Tags",
        tickers: "Symboles",
        showAnalysisRaw: "Afficher les données brutes de l’analyse",
        hideAnalysisRaw: "Masquer les données brutes de l’analyse",
        showTranslationsRaw:
            "Afficher les données brutes des traductions",
        hideTranslationsRaw:
            "Masquer les données brutes des traductions",
        unknown: "Inconnu",
        actionBuy: "ACHETER",
        actionSell: "VENDRE",
        actionHold: "CONSERVER",
        horizonShort: "Court terme",
        horizonMedium: "Moyen terme",
        horizonLong: "Long terme",
        horizonIntra: "Intrajournalier"
    },

    es: {
        unknownProvider: "Proveedor desconocido",
        noTitle: "Sin título",
        openOriginal: "Abrir original",
        recommendation: "Recomendación",
        noAnalysis: "No hay análisis disponible.",
        analysisJsonInvalid:
            "No se pudo leer el JSON del análisis.",
        action: "Acción",
        confidence: "Confianza",
        horizon: "Horizonte",
        analysis: "Análisis",
        rationale: "Justificación",
        tags: "Etiquetas",
        tickers: "Símbolos",
        showAnalysisRaw: "Mostrar datos sin procesar del análisis",
        hideAnalysisRaw: "Ocultar datos sin procesar del análisis",
        showTranslationsRaw:
            "Mostrar datos sin procesar de las traducciones",
        hideTranslationsRaw:
            "Ocultar datos sin procesar de las traducciones",
        unknown: "Desconocido",
        actionBuy: "COMPRAR",
        actionSell: "VENDER",
        actionHold: "MANTENER",
        horizonShort: "Corto plazo",
        horizonMedium: "Medio plazo",
        horizonLong: "Largo plazo",
        horizonIntra: "Intradía"
    },

    uk: {
        unknownProvider: "Невідомий постачальник",
        noTitle: "Без назви",
        openOriginal: "Відкрити оригінал",
        recommendation: "Рекомендація",
        noAnalysis: "Аналіз відсутній.",
        analysisJsonInvalid:
            "Не вдалося прочитати JSON аналізу.",
        action: "Дія",
        confidence: "Впевненість",
        horizon: "Горизонт",
        analysis: "Аналіз",
        rationale: "Обґрунтування",
        tags: "Теги",
        tickers: "Тикери",
        showAnalysisRaw: "Показати необроблені дані аналізу",
        hideAnalysisRaw: "Приховати необроблені дані аналізу",
        showTranslationsRaw:
            "Показати необроблені дані перекладів",
        hideTranslationsRaw:
            "Приховати необроблені дані перекладів",
        unknown: "Невідомо",
        actionBuy: "КУПУВАТИ",
        actionSell: "ПРОДАВАТИ",
        actionHold: "УТРИМУВАТИ",
        horizonShort: "Короткостроковий",
        horizonMedium: "Середньостроковий",
        horizonLong: "Довгостроковий",
        horizonIntra: "Внутрішньоденний"
    },

    tr: {
        unknownProvider: "Bilinmeyen sağlayıcı",
        noTitle: "Başlıksız",
        openOriginal: "Orijinali aç",
        recommendation: "Öneri",
        noAnalysis: "Analiz mevcut değil.",
        analysisJsonInvalid:
            "Analiz JSON verisi okunamadı.",
        action: "İşlem",
        confidence: "Güven",
        horizon: "Vade",
        analysis: "Analiz",
        rationale: "Gerekçe",
        tags: "Etiketler",
        tickers: "Semboller",
        showAnalysisRaw: "Analiz ham verilerini göster",
        hideAnalysisRaw: "Analiz ham verilerini gizle",
        showTranslationsRaw:
            "Çeviri ham verilerini göster",
        hideTranslationsRaw:
            "Çeviri ham verilerini gizle",
        unknown: "Bilinmiyor",
        actionBuy: "AL",
        actionSell: "SAT",
        actionHold: "TUT",
        horizonShort: "Kısa vadeli",
        horizonMedium: "Orta vadeli",
        horizonLong: "Uzun vadeli",
        horizonIntra: "Gün içi"
    }
};

function normalizeLanguage(value) {
    const language = String(value || DEFAULT_LANGUAGE)
        .trim()
        .toLowerCase()
        .split("-")[0];

    return UI_TEXTS[language]
        ? language
        : DEFAULT_LANGUAGE;
}

function normalizeTranslationLanguage(value) {
    const normalized = String(value || "")
        .trim()
        .toLowerCase()
        .split("-")[0];

    if (normalized === "org") {
        return "org";
    }

    if (normalized === "ua") {
        return "uk";
    }

    return UI_TEXTS[normalized]
        ? normalized
        : "";
}

function stripCodeFence(value) {
    if (!value || typeof value !== "string") {
        return "";
    }

    let text = value.trim();

    if (!text.startsWith("```")) {
        return text;
    }

    const lines = text.split(/\r?\n/);
    lines.shift();

    if (
        lines.length > 0 &&
        lines[lines.length - 1].trim().startsWith("```")
    ) {
        lines.pop();
    }

    return lines.join("\n").trim();
}

function extractJsonSection(
    value,
    openingCharacter,
    closingCharacter
) {
    if (!value) {
        return value;
    }

    const start = value.indexOf(openingCharacter);
    const end = value.lastIndexOf(closingCharacter);

    if (start !== -1 && end !== -1 && end > start) {
        return value.substring(start, end + 1);
    }

    return value;
}

function safeParseAnalysisJson(value) {
    if (!value) {
        return null;
    }

    if (
        typeof value === "object" &&
        !Array.isArray(value)
    ) {
        return value;
    }

    if (typeof value !== "string") {
        return null;
    }

    try {
        const withoutCodeFence = stripCodeFence(value);

        const jsonText = extractJsonSection(
            withoutCodeFence,
            "{",
            "}"
        );

        const parsed = JSON.parse(jsonText);

        return (
            parsed &&
            typeof parsed === "object" &&
            !Array.isArray(parsed)
        )
            ? parsed
            : null;
    } catch (error) {
        console.warn(
            "Analysis.Json konnte nicht gelesen werden:",
            error
        );

        return null;
    }
}

function safeParseTranslationsJson(value) {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value === "object") {
        if (Array.isArray(value.translations)) {
            return value.translations;
        }

        return [];
    }

    if (typeof value !== "string") {
        return [];
    }

    try {
        const withoutCodeFence = stripCodeFence(value);

        const jsonText = extractJsonSection(
            withoutCodeFence,
            "[",
            "]"
        );

        const parsed = JSON.parse(jsonText);

        if (Array.isArray(parsed)) {
            return parsed;
        }

        return Array.isArray(parsed?.translations)
            ? parsed.translations
            : [];
    } catch (error) {
        console.warn(
            "TranslationsJson konnte nicht gelesen werden:",
            error
        );

        return [];
    }
}

function getTranslationsJson(item) {
    return (
        item?.translationsJson ??
        item?.translationJson ??
        item?.TranslationsJson ??
        item?.TranslationJson ??
        null
    );
}

function getLocalizedObjectValue(
    value,
    language,
    fallback = ""
) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    if (typeof value === "string") {
        return value;
    }

    if (
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value
            .map(entry =>
                getLocalizedObjectValue(
                    entry,
                    language,
                    ""
                )
            )
            .filter(Boolean)
            .join("\n");
    }

    if (typeof value !== "object") {
        return fallback;
    }

    const languageKeys = {
        en: ["en", "english"],
        de: ["de", "german", "deutsch"],
        fr: ["fr", "french", "français"],
        es: ["es", "spanish", "español"],
        uk: ["uk", "ua", "ukrainian"],
        tr: ["tr", "turkish", "türkçe"]
    };

    const keys = languageKeys[language] || languageKeys.en;

    for (const key of keys) {
        if (
            value[key] !== null &&
            value[key] !== undefined &&
            value[key] !== ""
        ) {
            return getLocalizedObjectValue(
                value[key],
                language,
                fallback
            );
        }
    }

    const englishKeys = languageKeys.en;

    for (const key of englishKeys) {
        if (
            value[key] !== null &&
            value[key] !== undefined &&
            value[key] !== ""
        ) {
            return getLocalizedObjectValue(
                value[key],
                DEFAULT_LANGUAGE,
                fallback
            );
        }
    }

    const genericKeys = [
        "text",
        "summary",
        "description",
        "analysis",
        "analyse",
        "rationale",
        "value",
        "label",
        "name",
        "title"
    ];

    for (const key of genericKeys) {
        if (
            value[key] !== null &&
            value[key] !== undefined &&
            value[key] !== ""
        ) {
            return getLocalizedObjectValue(
                value[key],
                language,
                fallback
            );
        }
    }

    const firstTextValue = Object.values(value).find(
        entry =>
            typeof entry === "string" &&
            entry.trim().length > 0
    );

    return firstTextValue || fallback;
}


function getLocalizedAnalysisValue(
    value,
    language,
    isGuest,
    fallback = ""
) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    if (typeof value === "string") {
        return value;
    }

    if (
        typeof value === "number" ||
        typeof value === "boolean"
    ) {
        return String(value);
    }

    if (Array.isArray(value)) {
        return value
            .map(entry =>
                getLocalizedAnalysisValue(
                    entry,
                    language,
                    isGuest,
                    ""
                )
            )
            .filter(Boolean)
            .join("\n");
    }

    if (typeof value !== "object") {
        return fallback;
    }

    const languageKeys = {
        en: ["en", "english"],
        de: ["de", "german", "deutsch"],
        fr: ["fr", "french", "français"],
        es: ["es", "spanish", "español"],
        uk: ["uk", "ua", "ukrainian", "ukrainish"],
        tr: ["tr", "turkish", "türkçe"]
    };

    /*
     * Gäste sehen die von OpenAiAnalysisService unter "org"
     * gespeicherte Originalfassung. Es wird für Gäste keine
     * englische oder andere Übersetzung erzwungen.
     */
    if (isGuest) {
        const originalKeys = [
            "org",
            "original",
            "originalText",
            "originalAnalysis",
            "originalRationale"
        ];

        for (const key of originalKeys) {
            if (
                value[key] !== null &&
                value[key] !== undefined &&
                value[key] !== ""
            ) {
                return getLocalizedAnalysisValue(
                    value[key],
                    language,
                    true,
                    fallback
                );
            }
        }

        /*
         * Kompatibilität mit älteren Analysen ohne "org":
         * Vorhandenen Text anzeigen, aber nichts neu übersetzen.
         */
        const firstExistingText = Object.values(value).find(
            entry =>
                typeof entry === "string" &&
                entry.trim().length > 0
        );

        return firstExistingText || fallback;
    }

    const selectedKeys =
        languageKeys[language] ||
        languageKeys[DEFAULT_LANGUAGE];

    for (const key of selectedKeys) {
        if (
            value[key] !== null &&
            value[key] !== undefined &&
            value[key] !== ""
        ) {
            return getLocalizedAnalysisValue(
                value[key],
                language,
                false,
                fallback
            );
        }
    }

    /*
     * Für angemeldete Benutzer:
     * gewählte Sprache -> Englisch -> Original.
     */
    for (const key of ["en", "english", "org"]) {
        if (
            value[key] !== null &&
            value[key] !== undefined &&
            value[key] !== ""
        ) {
            return getLocalizedAnalysisValue(
                value[key],
                DEFAULT_LANGUAGE,
                false,
                fallback
            );
        }
    }

    const genericKeys = [
        "text",
        "summary",
        "description",
        "analysis",
        "analyse",
        "rationale",
        "value"
    ];

    for (const key of genericKeys) {
        if (
            value[key] !== null &&
            value[key] !== undefined &&
            value[key] !== ""
        ) {
            return getLocalizedAnalysisValue(
                value[key],
                language,
                false,
                fallback
            );
        }
    }

    const firstExistingText = Object.values(value).find(
        entry =>
            typeof entry === "string" &&
            entry.trim().length > 0
    );

    return firstExistingText || fallback;
}

function getLocalizedNews(
    item,
    translations,
    language,
    texts,
    isGuest
) {
    let selectedTranslation = null;

    if (isGuest) {
        /*
         * Gäste sehen immer die Originalfassung.
         */
        selectedTranslation =
            translations.find(translation => {
                const translationLanguage =
                    normalizeTranslationLanguage(
                        translation?.lang ??
                        translation?.language ??
                        translation?.languageCode
                    );

                return translationLanguage === "org";
            }) || null;
    } else {
        /*
         * Angemeldete Benutzer sehen die ausgewählte Sprache.
         */
        selectedTranslation =
            translations.find(translation => {
                const translationLanguage =
                    normalizeTranslationLanguage(
                        translation?.lang ??
                        translation?.language ??
                        translation?.languageCode
                    );

                return translationLanguage === language;
            }) || null;

        /*
         * Fallback auf Englisch.
         */
        if (!selectedTranslation) {
            selectedTranslation =
                translations.find(translation => {
                    const translationLanguage =
                        normalizeTranslationLanguage(
                            translation?.lang ??
                            translation?.language ??
                            translation?.languageCode
                        );

                    return (
                        translationLanguage ===
                        DEFAULT_LANGUAGE
                    );
                }) || null;
        }

        /*
         * Letzter Fallback auf Original.
         */
        if (!selectedTranslation) {
            selectedTranslation =
                translations.find(translation => {
                    const translationLanguage =
                        normalizeTranslationLanguage(
                            translation?.lang ??
                            translation?.language ??
                            translation?.languageCode
                        );

                    return translationLanguage === "org";
                }) || null;
        }
    }

    return {
        title:
            getLocalizedObjectValue(
                selectedTranslation?.title ??
                selectedTranslation?.Title,
                language,
                ""
            ) ||
            getLocalizedObjectValue(
                item?.title ??
                item?.Title,
                language,
                texts.noTitle
            ),

        summary:
            getLocalizedObjectValue(
                selectedTranslation?.summary ??
                selectedTranslation?.Summary,
                language,
                ""
            ) ||
            getLocalizedObjectValue(
                item?.summary ??
                item?.Summary,
                language,
                ""
            )
    };
}

function normalizeAction(value) {
    const text = getLocalizedObjectValue(
        value,
        DEFAULT_LANGUAGE,
        ""
    )
        .trim()
        .toLowerCase();

    if (
        text.includes("buy") ||
        text.includes("kaufen") ||
        text === "al"
    ) {
        return "buy";
    }

    if (
        text.includes("sell") ||
        text.includes("verkaufen") ||
        text === "sat"
    ) {
        return "sell";
    }

    if (
        text.includes("hold") ||
        text.includes("halten") ||
        text.includes("conserver") ||
        text.includes("mantener") ||
        text.includes("утримувати") ||
        text === "tut"
    ) {
        return "hold";
    }

    return text || "unknown";
}

function normalizeConfidence(value) {
    if (typeof value === "number") {
        return value > 1
            ? value / 100
            : value;
    }

    if (value && typeof value === "object") {
        return normalizeConfidence(
            value.confidence ??
            value.value
        );
    }

    const parsed = Number(
        String(value ?? "")
            .trim()
            .replace(",", ".")
            .replace("%", "")
    );

    if (!Number.isFinite(parsed)) {
        return 0;
    }

    return parsed > 1
        ? parsed / 100
        : parsed;
}

function normalizeTicker(value) {
    if (!value) {
        return null;
    }

    const ticker = String(value)
        .trim()
        .toUpperCase();

    if (!/^[A-Z0-9.^=-]+$/.test(ticker)) {
        return null;
    }

    if (ticker.length < 2) {
        return null;
    }

    return ticker;
}

function getActionColor(action) {
    switch (action) {
        case "buy":
            return "#2e7d32";

        case "sell":
            return "#c62828";

        case "hold":
            return "#0277bd";

        default:
            return "#616161";
    }
}

function getLocalizedAction(action, texts) {
    switch (action) {
        case "buy":
            return texts.actionBuy;

        case "sell":
            return texts.actionSell;

        case "hold":
            return texts.actionHold;

        default:
            return texts.unknown;
    }
}

function getLocalizedHorizon(value, language, texts) {
    const rawValue = getLocalizedObjectValue(
        value,
        language,
        ""
    );

    const normalizedValue = rawValue
        .trim()
        .toLowerCase();

    if (
        normalizedValue === "short" ||
        normalizedValue.includes("short-term") ||
        normalizedValue.includes("kurz")
    ) {
        return texts.horizonShort;
    }

    if (
        normalizedValue === "medium" ||
        normalizedValue.includes("medium-term") ||
        normalizedValue.includes("mittel")
    ) {
        return texts.horizonMedium;
    }

    if (
        normalizedValue === "long" ||
        normalizedValue.includes("long-term") ||
        normalizedValue.includes("lang")
    ) {
        return texts.horizonLong;
    }

    if (
        normalizedValue === "intra" ||
        normalizedValue === "intraday" ||
        normalizedValue.includes("day")
    ) {
        return texts.horizonIntra;
    }

    return rawValue || "–";
}

function parsePublishedDate(value) {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? null
            : value;
    }

    const rawValue = String(value).trim();

    if (!rawValue) {
        return null;
    }

    const normalizedValue = rawValue.includes("T")
        ? rawValue
        : rawValue.replace(" ", "T");

    const hasTimezone =
        /Z$/i.test(normalizedValue) ||
        /[+-]\d{2}:\d{2}$/.test(normalizedValue);

    const parsed = new Date(
        hasTimezone
            ? normalizedValue
            : `${normalizedValue}Z`
    );

    return Number.isNaN(parsed.getTime())
        ? null
        : parsed;
}

function formatPublishedAt(value, language) {
    if (!value) {
        return "";
    }

    const date = parsePublishedDate(value);

    if (!date) {
        return String(value);
    }

    const locale =
        LANGUAGE_LOCALES[language] ||
        LANGUAGE_LOCALES[DEFAULT_LANGUAGE];

    return date.toLocaleString(locale, {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}

export default function NewsAnalysisCard({ item }) {
    const { language } = useLanguage();
    const { isGuest } = useAuth();

    const selectedLanguage =
        normalizeLanguage(language);

    const texts =
        UI_TEXTS[selectedLanguage] ||
        UI_TEXTS[DEFAULT_LANGUAGE];

    const [showAnalysisRaw, setShowAnalysisRaw] =
        useState(false);

    const [showTranslationsRaw, setShowTranslationsRaw] =
        useState(false);

    const analysis = item?.analysis;

    const analysisJson =
        analysis?.json ??
        analysis?.Json ??
        item?.analysisJson ??
        item?.AnalysisJson ??
        null;

    const parsed = useMemo(
        () => safeParseAnalysisJson(analysisJson),
        [analysisJson]
    );

    const translations = useMemo(
        () =>
            safeParseTranslationsJson(
                getTranslationsJson(item)
            ),
        [
            item?.translationsJson,
            item?.translationJson,
            item?.TranslationsJson,
            item?.TranslationJson
        ]
    );

    const localizedNews = useMemo(
        () =>
            getLocalizedNews(
                item,
                translations,
                selectedLanguage,
                texts,
                isGuest
            ),
        [
            item,
            translations,
            selectedLanguage,
            texts,
            isGuest
        ]
    );

    useEffect(() => {
        setShowAnalysisRaw(false);
        setShowTranslationsRaw(false);
    }, [item?.id]);

    const action = normalizeAction(
        parsed?.action ??
        parsed?.analysis?.action ??
        parsed?.recommendation?.action
    );

    const confidence = normalizeConfidence(
        parsed?.confidence ??
        parsed?.analysis?.confidence ??
        parsed?.recommendation?.confidence
    );

    const horizon = getLocalizedHorizon(
        parsed?.horizon ??
        parsed?.analysis?.horizon ??
        parsed?.recommendation?.horizon,
        selectedLanguage,
        texts
    );

    const analysisText = getLocalizedAnalysisValue(
        parsed?.analysis?.text ??
        parsed?.analysis?.summary ??
        parsed?.analysis?.analysis ??
        parsed?.analysis ??
        parsed?.summary ??
        parsed?.text,
        selectedLanguage,
        isGuest,
        ""
    );

    const rationaleText = getLocalizedAnalysisValue(
        parsed?.rationale?.rationale ??
        parsed?.rationale ??
        parsed?.analysis?.rationale ??
        parsed?.recommendation?.rationale,
        selectedLanguage,
        isGuest,
        ""
    );

    const tags = useMemo(() => {
        const source = parsed?.tags;

        if (Array.isArray(source)) {
            return source
                .map(tag =>
                    getLocalizedObjectValue(
                        tag,
                        selectedLanguage,
                        ""
                    )
                )
                .filter(Boolean);
        }

        if (typeof source === "string") {
            return source
                .split(",")
                .map(tag => tag.trim())
                .filter(Boolean);
        }

        return [];
    }, [parsed, selectedLanguage]);

    const tickers = useMemo(() => {
        const source = parsed?.tickers;

        let tickerValues = [];

        if (Array.isArray(source)) {
            tickerValues = source;
        } else if (typeof source === "string") {
            tickerValues = source
                .split(",")
                .map(ticker => ticker.trim());
        } else if (
            source &&
            typeof source === "object"
        ) {
            tickerValues = [source];
        }

        const normalized = tickerValues
            .map(ticker =>
                normalizeTicker(
                    ticker?.symbol ??
                    ticker?.ticker ??
                    ticker
                )
            )
            .filter(Boolean);

        return [...new Set(normalized)];
    }, [parsed]);

    const provider = getLocalizedObjectValue(
        item?.provider,
        selectedLanguage,
        texts.unknownProvider
    );

    const publishedText = formatPublishedAt(
        item?.publishedAt,
        selectedLanguage
    );

    const actionColor = getActionColor(action);

    return (
        <Card style={{ marginBottom: 20 }}>
            <CardHeader
                title={localizedNews.title}
                subheader={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            marginTop: 6
                        }}
                    >
                        <span
                            style={{
                                background: "#e3f2fd",
                                color: "#0d47a1",
                                padding: "3px 9px",
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                marginRight: 8
                            }}
                        >
                            {provider}
                        </span>

                        {publishedText && (
                            <span
                                style={{
                                    color: "#757575",
                                    fontSize: 12
                                }}
                            >
                                {publishedText}
                            </span>
                        )}
                    </div>
                }
            />

            <CardContent>
                {localizedNews.summary && (
                    <Typography
                        variant="body1"
                        style={{
                            marginBottom: 12,
                            fontSize: 15,
                            lineHeight: 1.65,
                            whiteSpace: "pre-wrap"
                        }}
                    >
                        {localizedNews.summary}
                    </Typography>
                )}

                {item?.url && (
                    <Typography
                        variant="body2"
                        style={{ marginBottom: 12 }}
                    >
                        <Link
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {texts.openOriginal}
                        </Link>
                    </Typography>
                )}

                <Divider style={{ margin: "18px 0" }} />

                <Typography
                    variant="h6"
                    style={{ marginBottom: 10 }}
                >
                    {texts.recommendation}
                </Typography>

                {!analysis && !analysisJson && (
                    <Typography variant="body2">
                        {texts.noAnalysis}
                    </Typography>
                )}

                {(analysis || analysisJson) && !parsed && (
                    <Typography
                        variant="body2"
                        style={{ color: "#c62828" }}
                    >
                        {texts.analysisJsonInvalid}
                    </Typography>
                )}

                {parsed && (
                    <>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8
                            }}
                        >
                            <Chip
                                label={`${texts.action}: ${getLocalizedAction(
                                    action,
                                    texts
                                )}`}
                                style={{
                                    background: actionColor,
                                    color: "#fff",
                                    fontWeight: 600
                                }}
                            />

                            <Chip
                                label={`${texts.confidence}: ${Math.round(
                                    confidence * 100
                                )}%`}
                                style={{
                                    background: "#f5f5f5",
                                    color: "#424242"
                                }}
                            />

                            <Chip
                                label={`${texts.horizon}: ${horizon}`}
                                style={{
                                    background: "#f5f5f5",
                                    color: "#424242"
                                }}
                            />
                        </div>

                        {analysisText && (
                            <>
                                <Typography
                                    variant="h6"
                                    style={{
                                        marginTop: 22,
                                        marginBottom: 8
                                    }}
                                >
                                    {texts.analysis}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        marginBottom: 16,
                                        fontSize: 16,
                                        lineHeight: 1.7
                                    }}
                                >
                                    {analysisText}
                                </Typography>
                            </>
                        )}

                        {rationaleText && (
                            <>
                                <Typography
                                    variant="h6"
                                    style={{
                                        marginTop: 20,
                                        marginBottom: 8
                                    }}
                                >
                                    {texts.rationale}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        marginBottom: 16,
                                        fontSize: 17,
                                        lineHeight: 1.75
                                    }}
                                >
                                    {rationaleText}
                                </Typography>
                            </>
                        )}

                        {tags.length > 0 && (
                            <>
                                <Typography
                                    variant="h6"
                                    style={{
                                        marginTop: 18,
                                        marginBottom: 8
                                    }}
                                >
                                    {texts.tags}
                                </Typography>

                                <div style={{ marginBottom: 12 }}>
                                    {tags.map((tag, index) => (
                                        <Chip
                                            key={`${tag}-${index}`}
                                            label={tag}
                                            style={{
                                                marginRight: 6,
                                                marginBottom: 6,
                                                background: "#eeeeee",
                                                color: "#424242"
                                            }}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        {tickers.length > 0 && (
                            <>
                                <Typography
                                    variant="h6"
                                    style={{
                                        marginTop: 18,
                                        marginBottom: 8
                                    }}
                                >
                                    {texts.tickers}
                                </Typography>

                                <div style={{ marginBottom: 12 }}>
                                    {tickers.map(ticker => (
                                        <Chip
                                            key={ticker}
                                            label={ticker}
                                            style={{
                                                marginRight: 6,
                                                marginBottom: 6,
                                                background: "#e0f7fa",
                                                color: "#00796b",
                                                fontWeight: 700,
                                                textTransform: "uppercase"
                                            }}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                                setShowAnalysisRaw(
                                    currentValue =>
                                        !currentValue
                                )
                            }
                            style={{
                                marginTop: 10,
                                marginRight: 8
                            }}
                        >
                            {showAnalysisRaw
                                ? texts.hideAnalysisRaw
                                : texts.showAnalysisRaw}
                        </Button>

                        {translations.length > 0 && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                    setShowTranslationsRaw(
                                        currentValue =>
                                            !currentValue
                                    )
                                }
                                style={{ marginTop: 10 }}
                            >
                                {showTranslationsRaw
                                    ? texts.hideTranslationsRaw
                                    : texts.showTranslationsRaw}
                            </Button>
                        )}

                        <Collapse in={showAnalysisRaw}>
                            <pre
                                style={{
                                    background: "#f5f5f5",
                                    padding: 12,
                                    marginTop: 10,
                                    borderRadius: 6,
                                    maxHeight: 340,
                                    overflow: "auto",
                                    fontSize: 12,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word"
                                }}
                            >
                                {JSON.stringify(
                                    parsed,
                                    null,
                                    2
                                )}
                            </pre>
                        </Collapse>

                        <Collapse in={showTranslationsRaw}>
                            <pre
                                style={{
                                    background: "#f5f5f5",
                                    padding: 12,
                                    marginTop: 10,
                                    borderRadius: 6,
                                    maxHeight: 340,
                                    overflow: "auto",
                                    fontSize: 12,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word"
                                }}
                            >
                                {JSON.stringify(
                                    translations,
                                    null,
                                    2
                                )}
                            </pre>
                        </Collapse>
                    </>
                )}
            </CardContent>
        </Card>
    );
}